import {
  type AuthErrorCode,
  type AuthErrorResponse,
  AuthErrorResponseSchema,
  type AuthMeResponse,
  AuthMeResponseSchema,
  type AuthSuccessResponse,
  AuthSuccessResponseSchema,
  type CorpsDirectoryResponse,
  CorpsDirectoryResponseSchema,
  type EmailVerifyBody,
  type LeaderboardAxis,
  type LeaderboardResponse,
  LeaderboardResponseSchema,
  type LoginBody,
  type PasswordResetConfirmBody,
  type PasswordResetRequestBody,
  type PublicProfile,
  PublicProfileResponseSchema,
  type PublicUser,
  type SignupBody,
} from '@sfco/shared';

/**
 * Base URL for auth endpoints.
 *
 * - Dev: Vite at :5173, server at :3001 — set `VITE_AUTH_BASE=http://localhost:3001`.
 * - Prod (Phase 0.5): the Fastify server serves the built client, so same-origin
 *   requests work with `VITE_AUTH_BASE=''` (the default).
 */
export const AUTH_API_BASE: string =
  (import.meta.env.VITE_AUTH_BASE as string | undefined) ?? '';

export type AuthApiErrorCode = AuthErrorCode | 'NETWORK' | 'UNKNOWN';

export class AuthApiError extends Error {
  constructor(
    public readonly code: AuthApiErrorCode,
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

interface RequestOptions {
  method: 'GET' | 'POST';
  body?: unknown;
}

async function rawFetch(path: string, opts: RequestOptions): Promise<Response> {
  const url = `${AUTH_API_BASE}${path}`;
  const init: RequestInit = {
    method: opts.method,
    credentials: 'include',
    ...(opts.body !== undefined
      ? { headers: { 'Content-Type': 'application/json' } }
      : {}),
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

  try {
    return await fetch(url, init);
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Network request failed';
    throw new AuthApiError('NETWORK', 0, message);
  }
}

async function parseError(res: Response): Promise<AuthApiError> {
  try {
    const json = (await res.json()) as unknown;
    const parsed = AuthErrorResponseSchema.safeParse(json);
    if (parsed.success) {
      const body: AuthErrorResponse = parsed.data;
      return new AuthApiError(body.error, res.status, body.message);
    }
  } catch {
    // fall through
  }
  return new AuthApiError('UNKNOWN', res.status, `Request failed (${res.status})`);
}

export async function fetchMe(): Promise<PublicUser | null> {
  const res = await rawFetch('/auth/me', { method: 'GET' });
  if (!res.ok) {
    // /auth/me always 200s in spec; surface anything else as UNKNOWN.
    throw await parseError(res);
  }
  const json = (await res.json()) as unknown;
  const parsed = AuthMeResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new AuthApiError('UNKNOWN', res.status, 'Malformed /auth/me response');
  }
  const data: AuthMeResponse = parsed.data;
  return data.user;
}

async function postUser(path: string, body: unknown): Promise<PublicUser> {
  const res = await rawFetch(path, { method: 'POST', body });
  if (!res.ok) throw await parseError(res);
  const json = (await res.json()) as unknown;
  const parsed = AuthSuccessResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new AuthApiError('UNKNOWN', res.status, 'Malformed auth response');
  }
  const data: AuthSuccessResponse = parsed.data;
  return data.user;
}

async function postNoContent(path: string, body: unknown): Promise<void> {
  const res = await rawFetch(path, { method: 'POST', body });
  if (!res.ok) throw await parseError(res);
}

export function signupRequest(body: SignupBody): Promise<PublicUser> {
  return postUser('/auth/signup', body);
}

export function loginRequest(body: LoginBody): Promise<PublicUser> {
  return postUser('/auth/login', body);
}

export async function logoutRequest(): Promise<void> {
  const res = await rawFetch('/auth/logout', { method: 'POST' });
  if (!res.ok && res.status !== 204) throw await parseError(res);
}

export function verifyEmailRequest(token: string): Promise<void> {
  const body: EmailVerifyBody = { token };
  return postNoContent('/auth/email/verify', body);
}

export function passwordResetRequestRequest(
  body: PasswordResetRequestBody,
): Promise<void> {
  return postNoContent('/auth/password/reset-request', body);
}

export function passwordResetConfirmRequest(
  body: PasswordResetConfirmBody,
): Promise<void> {
  return postNoContent('/auth/password/reset-confirm', body);
}

/** Public profile fetch — 404s when the user turned visibility off. */
export async function fetchPublicProfile(corpSlug: string): Promise<PublicProfile> {
  const res = await rawFetch(`/api/users/${encodeURIComponent(corpSlug)}/public`, {
    method: 'GET',
  });
  if (!res.ok) throw await parseError(res);
  const json = (await res.json()) as unknown;
  const parsed = PublicProfileResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new AuthApiError('UNKNOWN', res.status, 'Malformed profile response');
  }
  return parsed.data.profile;
}

// ---------- P7A leaderboard + corps directory ────────────────────────────

/** Fetch a leaderboard for one axis. Server caches 60s; safe to poll. */
export async function fetchLeaderboard(
  axis: LeaderboardAxis,
): Promise<LeaderboardResponse> {
  const res = await rawFetch(`/api/leaderboards/${encodeURIComponent(axis)}`, {
    method: 'GET',
  });
  if (!res.ok) throw await parseError(res);
  const json = (await res.json()) as unknown;
  const parsed = LeaderboardResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new AuthApiError('UNKNOWN', res.status, 'Malformed leaderboard response');
  }
  return parsed.data;
}

/** Browse the public corps directory. All filter args optional. */
export async function fetchCorpsDirectory(args: {
  q?: string;
  faction?: string;
  limit?: number;
  offset?: number;
}): Promise<CorpsDirectoryResponse> {
  const params = new URLSearchParams();
  if (args.q) params.set('q', args.q);
  if (args.faction) params.set('faction', args.faction);
  if (args.limit !== undefined) params.set('limit', String(args.limit));
  if (args.offset !== undefined) params.set('offset', String(args.offset));
  const qs = params.toString();
  const res = await rawFetch(`/api/corps${qs ? `?${qs}` : ''}`, { method: 'GET' });
  if (!res.ok) throw await parseError(res);
  const json = (await res.json()) as unknown;
  const parsed = CorpsDirectoryResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new AuthApiError('UNKNOWN', res.status, 'Malformed corps directory response');
  }
  return parsed.data;
}
